import { createAction } from '@reduxjs/toolkit';
import { graphql, readInlineData } from 'relay-runtime';

import { PANES, type Pane, TABS } from '../constants/checkoutConstants';
import {
    checkoutUIActionsTransactionSetup_viewer$key,
    checkoutUIActionsTransactionSetup_viewer$data,
} from './__generated__/checkoutUIActionsTransactionSetup_viewer.graphql';

import {
    checkoutUIActionsTransactionSetup_buyer$key,
    checkoutUIActionsTransactionSetup_buyer$data,
} from './__generated__/checkoutUIActionsTransactionSetup_buyer.graphql';

import {
    checkoutUIActionsTransactionSetup_transaction$key,
    checkoutUIActionsTransactionSetup_transaction$data,
} from './__generated__/checkoutUIActionsTransactionSetup_transaction.graphql';

import {
    checkoutUIActions_deliveryOptions$key,
    checkoutUIActions_deliveryOptions$data,
} from './__generated__/checkoutUIActions_deliveryOptions.graphql';
import {
    checkoutUIActions_preCheckoutTransaction$key,
    checkoutUIActions_preCheckoutTransaction$data,
} from './__generated__/checkoutUIActions_preCheckoutTransaction.graphql';
import {
    checkoutUIActions_buyer$key,
    checkoutUIActions_buyer$data,
} from './__generated__/checkoutUIActions_buyer.graphql';

import { trackSidemark } from '../helpers/checkoutTracking';
import {
    navigateDeliveryAction,
    navigatePaymentAction,
    navigateReviewAction,
    navigateShippingAction,
    setQuantityAction,
} from './commonActions';
import { Banner } from '../shared/banners';
import { AppDispatch } from '../reducers/root';
import { Currency } from '../components/shared/__generated__/CheckoutBuyerChargeCurrency_viewer.graphql';
import {
    editProjectAction,
    editSideMarkAction,
    toggleSummaryAction,
} from '../reducers/checkoutPaneReducer';
import { PlacementOptions as GetHelpModalPlacmentOptions } from 'dibs-contact-1stdibs/exports/GetHelpModalPlacements';
import { Step as GetHelpModalStep } from 'dibs-contact-1stdibs/exports/GetHelpModal';
import { PrerenderedCheckoutRootState } from '../prerender/prerenderedCheckoutStore';

type Action<Type extends string, Payload = Record<string, unknown>> = {
    type: Type;
} & Payload;

export const transactionSetupAction = createAction<
    (payload: {
        viewer: checkoutUIActionsTransactionSetup_viewer$key;
        buyer: checkoutUIActionsTransactionSetup_buyer$key | null;
        transaction: checkoutUIActionsTransactionSetup_transaction$key | null;
    }) => {
        payload: {
            viewer: checkoutUIActionsTransactionSetup_viewer$data;
            buyer: checkoutUIActionsTransactionSetup_buyer$data | null;
            transaction: checkoutUIActionsTransactionSetup_transaction$data | null;
        };
    }
>(
    'CHECKOUT_TRANSACTION_SETUP',
    ({ viewer: viewerRef, buyer: buyerRef, transaction: transactionRef }) => {
        const viewer = readInlineData(
            graphql`
                fragment checkoutUIActionsTransactionSetup_viewer on Viewer @inline {
                    ...checkoutUIReducer_viewer
                }
            `,
            viewerRef
        );

        const transaction = readInlineData(
            graphql`
                fragment checkoutUIActionsTransactionSetup_transaction on Transaction @inline {
                    ...checkoutUIReducer_transaction
                    ...checkoutTransactionReducer_transaction
                }
            `,
            transactionRef
        );

        const buyer = readInlineData(
            graphql`
                fragment checkoutUIActionsTransactionSetup_buyer on User @inline {
                    ...checkoutUIReducer_buyer
                }
            `,
            buyerRef
        );

        return { payload: { viewer, buyer, transaction } };
    }
);
export const toggleMarketingOptInAction = createAction('TOGGLE_MARKETING_OPT_IN');
export const setQuantityErrorAction = createAction<{
    hasQuantityError: boolean;
}>('SET_QUANTITY_ERROR');
export const toggleAcknowledgedRestrictionsAction = createAction(
    'TOGGLE_ACKNOWLEDGED_RESTRICTIONS'
);
export const checkoutErrorShownAction = createAction('CHECKOUT_ERROR_SHOWN');
export const startCheckoutProcessingAction = createAction('START_CHECKOUT_PROCESSING');
export const toggleProcessingOffer = createAction<boolean>('TOGGLE_CHECKOUT_PROCESSING_OFFER');

export const updateDeliveryOptions = createAction<
    (payload: {
        deliveryOptions: checkoutUIActions_deliveryOptions$key[];
        preCheckoutTransaction: checkoutUIActions_preCheckoutTransaction$key | null;
        buyer: checkoutUIActions_buyer$key | null;
    }) => {
        payload: {
            deliveryOptions: checkoutUIActions_deliveryOptions$data[];
            preCheckoutTransaction: checkoutUIActions_preCheckoutTransaction$data | null;
            buyer: checkoutUIActions_buyer$data | null;
        };
    }
>(
    'CHECKOUT_UPDATE_DELIVERY_OPTIONS',
    ({
        deliveryOptions: deliveryOptionsRefs,
        preCheckoutTransaction: preCheckoutTransactionRef,
        buyer: buyerRef,
        ...payload
    }) => {
        const deliveryOptions = (deliveryOptionsRefs || []).map(deliveryOptionRef =>
            readInlineData(
                graphql`
                    fragment checkoutUIActions_deliveryOptions on ShipmentQuote @inline {
                        ...checkoutTransactionReducer_deliveryOptions
                    }
                `,
                deliveryOptionRef
            )
        );
        const preCheckoutTransaction = readInlineData(
            graphql`
                fragment checkoutUIActions_preCheckoutTransaction on Transaction @inline {
                    ...checkoutTransactionReducer_preCheckoutTransaction
                }
            `,
            preCheckoutTransactionRef
        );
        const buyer = readInlineData(
            graphql`
                fragment checkoutUIActions_buyer on User @inline {
                    ...checkoutTransactionReducer_buyer
                }
            `,
            buyerRef
        );
        return { payload: { deliveryOptions, preCheckoutTransaction, buyer, ...payload } };
    }
);
export const deliveryOptionsComputing = createAction('CHECKOUT_DELIVERY_OPTIONS_COMPUTING');
export const setExitCheckoutAction = createAction<boolean>('SET_ALLOW_EXIT_CHECKOUT');
export const setAuthState = createAction<'IDLE' | 'LOADING' | 'CHECKED'>('SET_AUTH_STATE');
export const setTriggeredErrors = createAction<string[]>('SET_TRIGGERED_ERRORS');
export const toggleShippingNotesError = createAction<boolean>('TOGGLE_SHIPPING_NOTES_ERROR');

type NavigateToPanePayload = {
    pane: Pane;
    hasShippingAddresses: boolean;
    hasCreditCards: boolean;
    hasBankAccounts: boolean;
    isSummaryOpen: boolean;
    currentPane: Pane;
    isComputing: boolean;
    dispatch: AppDispatch;
};

export function navigateToPane(payload: NavigateToPanePayload): Action<string> | boolean {
    const {
        pane,
        hasShippingAddresses,
        hasCreditCards,
        hasBankAccounts,
        isSummaryOpen,
        currentPane,
        isComputing,
        dispatch,
    } = payload;
    const blockingPanes: Pane[] = [PANES.PANE_SHIPPING, PANES.PANE_DELIVERY];
    const isBlocked = isComputing && blockingPanes.includes(currentPane);

    dispatch({
        type: 'CHECKOUT_PANE_CHANGED',
        hasShippingAddresses,
        hasCreditCards,
        hasBankAccounts,
    });

    if (isBlocked) {
        return false;
    } else {
        switch (pane) {
            case PANES.PANE_SIDEMARK:
                return dispatch(editSideMarkAction());
            case PANES.PANE_SUMMARY:
                return dispatch(
                    toggleSummaryAction({
                        isSummaryOpen,
                    })
                );
            case PANES.PANE_PROJECT:
                return dispatch(editProjectAction());
            case PANES.PANE_SHIPPING:
                return dispatch(navigateShippingAction());
            case PANES.PANE_DELIVERY:
                return dispatch(navigateDeliveryAction());
            case PANES.PANE_PAYMENT:
                return dispatch(navigatePaymentAction());
            case PANES.PANE_REVIEW:
                return dispatch(navigateReviewAction());
            default:
                return false;
        }
    }
}

export const updateSidemarkStatusAction = createAction<{
    hasUnsavedSidemark: boolean;
}>('SET_SIDEMARK_STATUS');

export function saveSidemark(
    sidemark: string,
    action: 'save' | 'load'
): Action<
    'CHECKOUT_SET_SIDEMARK',
    {
        sidemark: string;
    }
> {
    if (action === 'save') {
        trackSidemark();
    }
    return {
        type: 'CHECKOUT_SET_SIDEMARK',
        sidemark,
    };
}

export function updateProject(payload: {
    projectName: string | null;
    projectId: string | null;
}): Action<
    'CHECKOUT_PROJECT_SET',
    {
        projectName: string | null;
        projectId: string | null;
    }
> {
    const { projectName = null, projectId = null } = payload || {};
    return { type: 'CHECKOUT_PROJECT_SET', projectName, projectId };
}

export function updateBuyerMessage(offerNotes: string): Action<
    'TRANSACTION_SET_OFFER_NOTES',
    {
        offerNotes: string;
    }
> {
    return {
        type: 'TRANSACTION_SET_OFFER_NOTES',
        offerNotes,
    };
}

type SelectedPaymentTabPayload = {
    paymentTab: (typeof TABS)[keyof typeof TABS];
    lastValidCreditCardToken: string | null;
    lastValidBankAccountToken: string | null;
    deselectCreditCards: boolean;
    deselectBankAccounts: boolean;
};
export function selectedPaymentTab(
    payload: SelectedPaymentTabPayload
): Action<'CHECKOUT_SELECT_PAYMENT_TAB', SelectedPaymentTabPayload> {
    const {
        paymentTab,
        lastValidCreditCardToken,
        lastValidBankAccountToken,
        deselectCreditCards,
        deselectBankAccounts,
    } = payload;
    return {
        type: 'CHECKOUT_SELECT_PAYMENT_TAB',
        paymentTab,
        lastValidCreditCardToken,
        lastValidBankAccountToken,
        deselectCreditCards,
        deselectBankAccounts,
    };
}

export function hasShownPaymentError(): Action<'CHECKOUT_PAYMENT_ERROR_SHOWN'> {
    return { type: 'CHECKOUT_PAYMENT_ERROR_SHOWN' };
}

export function changeQuantity(
    dispatch: AppDispatch,
    {
        quantity,
        paymentMethodToken,
    }: {
        quantity: number;
        paymentMethodToken: string;
    }
): void {
    dispatch(
        setQuantityAction({
            quantity,
            paymentMethodToken,
        })
    );

    dispatch(
        setQuantityErrorAction({
            hasQuantityError: !quantity,
        })
    );
}

export function setPromoCode(promoCode: string): Action<
    'TRANSACTION_SET_PROMO_CODE',
    {
        promoCode: string;
    }
> {
    return {
        type: 'TRANSACTION_SET_PROMO_CODE',
        promoCode,
    };
}

export function setBuyerChargeCurrency(buyerChargeCurrency: Currency): Action<
    'TRANSACTION_SET_CHARGE_CURRENCY',
    {
        buyerChargeCurrency: Currency;
    }
> {
    return {
        type: 'TRANSACTION_SET_CHARGE_CURRENCY',
        buyerChargeCurrency,
    };
}

export function removePromoCode(): Action<'TRANSACTION_REMOVE_PROMO_CODE'> {
    return {
        type: 'TRANSACTION_REMOVE_PROMO_CODE',
    };
}

export function showQuantityError(): ReturnType<typeof setQuantityErrorAction> {
    return setQuantityErrorAction({
        hasQuantityError: true,
    });
}

export const startAddressSaveAction = createAction('START_ADDRESS_SAVE');
export const finishAddressSaveAction = createAction('FINISH_ADDRESS_SAVE');
export const closeHeaderBannerAction = createAction('CLOSE_HEADER_BANNER');
export const setHeaderBannerAction = createAction<{
    banner: Banner;
}>('SET_HEADER_BANNER');
export const setWasNavigatedDueTaxWarningAction = createAction<boolean>(
    'SET_WAS_NAVIGATED_DUE_TAX_WARNING'
);

export function setIsGiftSelected(hasSelectedGift: boolean): Action<
    'TRANSACTION_SET_IS_GIFT_SELECTED',
    {
        hasSelectedGift: boolean;
    }
> {
    return {
        type: 'TRANSACTION_SET_IS_GIFT_SELECTED',
        hasSelectedGift,
    };
}

export function updateGiftMessage(
    giftMessage: string
): Action<'TRANSACTION_SET_GIFT_MESSAGE', { giftMessage: string }> {
    return {
        type: 'TRANSACTION_SET_GIFT_MESSAGE',
        giftMessage,
    };
}

export type CheckoutHelpType = 'GET_HELP' | 'LIVE_CHAT';
export const setCheckoutHelpType = createAction<CheckoutHelpType>('SET_CHECKOUT_HELP_TYPE');

export type CheckoutHelp = {
    showCheckoutHelp: boolean;
    placement?: GetHelpModalPlacmentOptions;
    step?: GetHelpModalStep;
};
export const setCheckoutHelp = createAction<CheckoutHelp>('SET_CHECKOUT_HELP');

export const setPreRenderState = createAction<PrerenderedCheckoutRootState>('SET_PRE_RENDER_STATE');
